import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Warning, ShieldCheck, HandPalm, Gift, ChatCircle, CheckCircle, X,
  ChartBar, Users, TrendDown
} from "@phosphor-icons/react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";

const CANCEL_REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "switched_competitor", label: "Switched to a competitor" },
  { value: "not_using_enough", label: "Not using it enough" },
  { value: "poor_support", label: "Poor customer support" },
  { value: "technical_issues", label: "Technical issues" },
  { value: "other", label: "Other reason" },
];

const PIE_COLORS = ["#3B82F6", "#EF4444", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#6B7280"];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold">{payload[0].name}: <span className="text-foreground">{payload[0].value}</span></p>
    </div>
  );
};

export default function DowngradeCancellationPage() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.is_platform_admin;

  // Cancel flow state
  const [step, setStep] = useState("initial"); // initial, reason, offer, confirm, done
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Admin analytics
  const [exitData, setExitData] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    if (isPlatformAdmin) {
      setAdminLoading(true);
      API.get("/analytics/exit-surveys")
        .then(({ data }) => setExitData(data))
        .catch(() => {})
        .finally(() => setAdminLoading(false));
    }
  }, [isPlatformAdmin]);

  const handleCancel = async (acceptOffer) => {
    setSubmitting(true);
    try {
      const { data } = await API.post("/billing/cancel", {
        reason,
        feedback,
        accept_offer: acceptOffer,
      });
      setResult(data);
      setStep("done");
      if (data.retention_applied) {
        toast.success("Great news! 30 free days added to your account.");
      } else if (data.cancelled) {
        toast("Subscription cancelled. Your data is preserved for 30 days.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process request");
    } finally {
      setSubmitting(false);
    }
  };

  // Build pie chart data from reason_breakdown
  const reasonPieData = exitData?.reason_breakdown
    ? Object.entries(exitData.reason_breakdown).map(([key, value]) => ({
        name: CANCEL_REASONS.find(r => r.value === key)?.label || key,
        value,
      }))
    : [];

  return (
    <div data-testid="downgrade-cancellation-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
          <HandPalm size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Manage Subscription</h1>
          <p className="text-xs text-muted-foreground">Downgrade or cancel your subscription</p>
        </div>
      </div>

      {/* User Cancellation Flow */}
      <div className="max-w-lg mb-8">
        {step === "initial" && (
          <div className="border border-border rounded-lg p-6 bg-card" data-testid="cancel-initial">
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} className="text-amber-500 shrink-0 mt-0.5" weight="duotone" />
              <div>
                <h3 className="text-sm font-semibold font-[Chivo] mb-1">Are you sure you want to cancel?</h3>
                <p className="text-xs text-muted-foreground">Before you go, we'd love to know what's not working so we can improve.</p>
              </div>
            </div>
            <Button variant="destructive" size="sm" onClick={() => setStep("reason")} data-testid="start-cancel-btn">
              Continue to Cancel
            </Button>
          </div>
        )}

        {step === "reason" && (
          <div className="border border-border rounded-lg p-6 bg-card" data-testid="cancel-reason-step">
            <h3 className="text-sm font-semibold font-[Chivo] mb-3">What's the main reason for leaving?</h3>
            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors
                    ${reason === r.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                  data-testid={`reason-${r.value}`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="accent-primary"
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Additional feedback (optional)</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Tell us more about your experience..."
                data-testid="cancel-feedback-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setStep("initial"); setReason(""); setFeedback(""); }} data-testid="cancel-back-btn">
                Back
              </Button>
              <Button variant="destructive" size="sm" disabled={!reason} onClick={() => setStep("offer")} data-testid="cancel-continue-btn">
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === "offer" && (
          <div className="border border-border rounded-lg p-6 bg-card" data-testid="cancel-offer-step">
            <div className="flex items-start gap-3 mb-4">
              <Gift size={28} className="text-emerald-500 shrink-0" weight="duotone" />
              <div>
                <h3 className="text-sm font-semibold font-[Chivo] mb-1">Wait — we have a special offer!</h3>
                <p className="text-xs text-muted-foreground">We'd hate to see you go. How about <strong className="text-foreground">30 free days</strong> on us?</p>
              </div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-md p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-emerald-600" weight="fill" />
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Retention Offer</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6">
                <li>30 additional free days added to your account</li>
                <li>Keep all your data and settings</li>
                <li>Full access to all your current plan features</li>
              </ul>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => handleCancel(true)} disabled={submitting} data-testid="accept-offer-btn">
                {submitting ? "Processing..." : "Accept Offer"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setStep("confirm")} disabled={submitting} data-testid="decline-offer-btn">
                No thanks, cancel anyway
              </Button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="border border-red-200 dark:border-red-800 rounded-lg p-6 bg-red-50 dark:bg-red-950/30" data-testid="cancel-confirm-step">
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} className="text-red-500 shrink-0 mt-0.5" weight="fill" />
              <div>
                <h3 className="text-sm font-semibold font-[Chivo] mb-1">Final Confirmation</h3>
                <p className="text-xs text-muted-foreground">
                  This will cancel your subscription immediately. Your data will be preserved for 30 days.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("offer")} disabled={submitting} data-testid="cancel-go-back-btn">
                Go Back
              </Button>
              <Button variant="destructive" size="sm" onClick={() => handleCancel(false)} disabled={submitting} data-testid="confirm-cancel-btn">
                {submitting ? "Cancelling..." : "Confirm Cancellation"}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="border border-border rounded-lg p-6 bg-card text-center" data-testid="cancel-done-step">
            {result.retention_applied ? (
              <>
                <Gift size={40} className="mx-auto text-emerald-500 mb-3" weight="duotone" />
                <h3 className="text-base font-semibold font-[Chivo] mb-1">Welcome Back!</h3>
                <p className="text-sm text-muted-foreground mb-3">{result.message}</p>
                <Button size="sm" onClick={() => setStep("initial")} data-testid="done-continue-btn">Continue</Button>
              </>
            ) : (
              <>
                <X size={40} className="mx-auto text-red-400 mb-3" />
                <h3 className="text-base font-semibold font-[Chivo] mb-1">Subscription Cancelled</h3>
                <p className="text-sm text-muted-foreground">{result.message}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Platform Admin: Exit Survey Analytics */}
      {isPlatformAdmin && (
        <div data-testid="admin-exit-analytics">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={18} className="text-violet-500" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Exit Survey Analytics</h2>
            <Badge variant="outline" className="text-[9px] ml-1">Admin</Badge>
          </div>

          {adminLoading ? (
            <div className="text-center py-10 text-muted-foreground text-sm">Loading analytics...</div>
          ) : exitData ? (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-4 gap-3 mb-6" data-testid="exit-kpis">
                <div className="border border-border rounded-lg p-4 bg-card">
                  <Users size={16} className="text-blue-500 mb-1" weight="duotone" />
                  <p className="text-xl font-bold font-[Chivo]">{exitData.total}</p>
                  <p className="text-[10px] text-muted-foreground">Total Surveys</p>
                </div>
                <div className="border border-border rounded-lg p-4 bg-card">
                  <CheckCircle size={16} className="text-emerald-500 mb-1" weight="duotone" />
                  <p className="text-xl font-bold font-[Chivo] text-emerald-600">{exitData.retained}</p>
                  <p className="text-[10px] text-muted-foreground">Retained</p>
                </div>
                <div className="border border-border rounded-lg p-4 bg-card">
                  <TrendDown size={16} className="text-red-500 mb-1" weight="duotone" />
                  <p className="text-xl font-bold font-[Chivo] text-red-500">{exitData.churned}</p>
                  <p className="text-[10px] text-muted-foreground">Churned</p>
                </div>
                <div className="border border-border rounded-lg p-4 bg-card">
                  <ShieldCheck size={16} className="text-violet-500 mb-1" weight="duotone" />
                  <p className="text-xl font-bold font-[Chivo] text-violet-600">{exitData.retention_rate}%</p>
                  <p className="text-[10px] text-muted-foreground">Retention Rate</p>
                </div>
              </div>

              {/* Reason Breakdown Chart */}
              {reasonPieData.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="border border-border rounded-lg p-4 bg-card" data-testid="reason-pie-chart">
                    <h3 className="text-sm font-semibold font-[Chivo] mb-3">Cancellation Reasons</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={reasonPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                          {reasonPieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="border border-border rounded-lg p-4 bg-card" data-testid="reason-bar-chart">
                    <h3 className="text-sm font-semibold font-[Chivo] mb-3">Reason Counts</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={reasonPieData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis type="number" tick={{ fontSize: 10 }} className="fill-muted-foreground" allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} className="fill-muted-foreground" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recent Exit Surveys */}
              {exitData.surveys?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden" data-testid="recent-exit-surveys">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border">
                    <h3 className="text-sm font-semibold font-[Chivo]">Recent Exit Surveys</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border bg-muted/10">
                          <th className="px-3 py-2 text-left font-semibold">Tenant</th>
                          <th className="px-3 py-2 text-left font-semibold">Plan</th>
                          <th className="px-3 py-2 text-left font-semibold">Reason</th>
                          <th className="px-3 py-2 text-center font-semibold">Retained?</th>
                          <th className="px-3 py-2 text-right font-semibold">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exitData.surveys.map((s, i) => (
                          <tr key={s.id || i} className="border-b border-border hover:bg-muted/10">
                            <td className="px-3 py-2 font-medium">{s.tenant_name || s.tenant_id?.slice(0, 8)}</td>
                            <td className="px-3 py-2 capitalize">{s.plan || "—"}</td>
                            <td className="px-3 py-2">{CANCEL_REASONS.find(r => r.value === s.reason)?.label || s.reason || "—"}</td>
                            <td className="px-3 py-2 text-center">
                              {s.accepted_retention_offer ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Yes</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[9px]">No</Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {s.created_at ? new Date(s.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No exit survey data available.</p>
          )}
        </div>
      )}
    </div>
  );
}
